import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ModalController, Platform } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import {
  Voting,
  VotingFile,
  VotingOption,
} from "src/model/class/general classes/voting";
import { AlertService } from "src/service/alert.service";
import { LoaderService } from "src/service/loader.service";
import { MediaService } from "src/service/media.service";
import { VotingService } from "src/service/voting.service";

interface EditableOption {
  id?: number;
  text: string;
}

/**
 * Create/edit modal for a voting (building administrator only). A voting can be
 * edited only while it is DRAFT (enforced server-side too). Supports a dynamic
 * list of answer options, a single/multiple choice toggle and the upload of
 * multiple attachments, each pushed straight to S3 via a presigned URL.
 */
@Component({
    selector: "app-popup-voting",
    templateUrl: "./popup-voting.component.html",
    styleUrls: ["./popup-voting.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PopupVotingComponent implements OnInit, OnDestroy {
  @Input() voting: Voting = null;

  public formVoting: UntypedFormGroup = null;
  public isEditing: boolean = false;
  public isLoading: boolean = false;

  public options: Array<EditableOption> = [{ text: "" }, { text: "" }];
  public existingFiles: Array<VotingFile> = [];
  public selectedFiles: Array<File> = [];

  // Bound to the ion-datetime overlays (ngModel, not formControlName, because
  // the pickers live inside ion-modals projected outside the form context).
  // ion-datetime consumes/produces ISO 8601 directly.
  public dateFromValue: string = "";
  public dateToValue: string = "";

  public newTitleLabel: string = "";
  public editTitleLabel: string = "";
  public titleFieldLabel: string = "";
  public descriptionLabel: string = "";
  public dateFromLabel: string = "";
  public dateToLabel: string = "";
  public multipleChoiceLabel: string = "";
  public answersLabel: string = "";
  public addAnswerLabel: string = "";
  public filesLabel: string = "";
  public newFilesLabel: string = "";
  public saveLabel: string = "";
  public cancelLabel: string = "";
  public doneLabel: string = "";
  public validationLabel: string = "";

  private _translationSubscription: Subscription = null;

  constructor(
    private _modalController: ModalController,
    private _formBuilder: UntypedFormBuilder,
    private _votingService: VotingService,
    private _alertService: AlertService,
    private _loaderService: LoaderService,
    private _translateService: TranslateService,
    private _mediaService: MediaService,
    private _platform: Platform,
  ) {}

  ngOnInit(): void {
    this.isEditing = !!this.voting?.id;
    this.initializeForm();
    this.loadTranslation();
    this.subscribeTranslationEvent();
  }

  ngOnDestroy(): void {
    this._translationSubscription?.unsubscribe();
  }

  /**
   * Append an empty answer option.
   */
  public addOption(): void {
    this.options.push({ text: "" });
  }

  /**
   * Remove an answer option (kept at a minimum of two).
   */
  public removeOption(index: number): void {
    if (this.options.length <= 2) return;
    this.options.splice(index, 1);
  }

  /**
   * Track the files picked in the file input.
   */
  public onFilesChange(event: any): void {
    const files = (event.target as HTMLInputElement).files;
    this.selectedFiles = files ? Array.from(files) : [];
  }

  /**
   * Drop a not-yet-uploaded file from the selection.
   */
  public removeSelectedFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  /**
   * Download an already-uploaded attachment (edit mode): fetch the serialized
   * buffer, rebuild the blob and save it via the platform-specific handler.
   */
  public async onClickDownloadFile(file: VotingFile): Promise<void> {
    if (!file?.id) return;

    try {
      await this._loaderService.presentLoaderWaiting();
      const responseData = await this._votingService.getUploadedFile(file.id);
      const blob: Blob = this._mediaService.jsonStringToBlob(responseData.data);

      if (this._platform.is("ios") || this._platform.is("android")) {
        await this._mediaService.handleMobileDownload(blob, file.filename);
      } else {
        this._mediaService.handleDesktopBrowserDownload(file.filename, blob);
      }
    } catch {
      await this._alertService.presentAlertSomethingWrong();
    } finally {
      await this._loaderService.dismissLoaderWaiting();
    }
  }

  /**
   * Delete an already-uploaded attachment (edit mode). Removes it server-side
   * (S3 + row) and from the local list.
   */
  public async removeExistingFile(file: VotingFile): Promise<void> {
    try {
      await this._loaderService.presentLoaderWaiting();
      await this._votingService.deleteFile(file.id);
      this.existingFiles = this.existingFiles.filter((f) => f.id !== file.id);
    } catch {
      await this._alertService.presentAlertSomethingWrong();
    } finally {
      await this._loaderService.dismissLoaderWaiting();
    }
  }

  public onCancel(): void {
    this._modalController.dismiss({ saved: false });
  }

  /**
   * Validate and persist the voting, then upload any newly selected files.
   * On the create flow a failed upload rolls back the just-created voting so it
   * does not linger without its attachments.
   */
  public async onClickSave(): Promise<void> {
    const cleanedOptions = this.options
      .map((option) => ({ ...option, text: (option.text || "").trim() }))
      .filter((option) => option.text.length > 0);

    if (this.formVoting.invalid || cleanedOptions.length < 2) {
      this.formVoting.markAllAsTouched();
      await this._alertService.presentAlertBillsQuotesCorrespondingMsg(
        false,
        "",
        this.validationLabel,
      );
      return;
    }

    if (!this.areDatesValid()) {
      await this._alertService.presentAlertBillsQuotesCorrespondingMsg(
        false,
        "",
        this.validationLabel,
      );
      return;
    }

    const payload: Partial<Voting> = {
      title: this.formVoting.value.title,
      description: this.formVoting.value.description || "",
      dateFrom: this.dateFromValue,
      dateTo: this.dateToValue,
      multipleChoice: !!this.formVoting.value.multipleChoice,
      options: cleanedOptions.map(
        (option, index): VotingOption => ({
          id: option.id,
          text: option.text,
          position: index,
        }),
      ),
    };

    try {
      this.isLoading = true;
      await this._loaderService.presentLoaderWaiting();

      let saved: Voting;
      if (this.isEditing && this.voting?.id) {
        saved = await this._votingService.updateVoting(this.voting.id, payload);
      } else {
        saved = await this._votingService.createVoting(payload);
      }

      if (this.selectedFiles.length && saved?.id) {
        await this.uploadSelectedFiles(saved.id, !this.isEditing);
      }

      this._modalController.dismiss({ saved: true });
    } catch {
      await this._alertService.presentAlertSomethingWrong();
    } finally {
      await this._loaderService.dismissLoaderWaiting();
      this.isLoading = false;
    }
  }

  /**
   * Upload every selected file straight to S3 (presigned PUT) and confirm each
   * binding. On the create flow, if any upload fails the whole voting is rolled
   * back so it is not left half-populated.
   * @param votingId id of the (just) saved voting
   * @param rollbackOnFailure whether to delete the voting if an upload fails
   */
  private async uploadSelectedFiles(
    votingId: number,
    rollbackOnFailure: boolean,
  ): Promise<void> {
    try {
      for (const file of this.selectedFiles) {
        const { url, idFile } = await this._votingService.getUploadUrl(
          votingId,
          file.name,
        );
        await this._votingService.uploadFileToPresignedUrl(url, file);
        await this._votingService.confirmUpload(idFile, file.name);
      }
    } catch (uploadError) {
      if (rollbackOnFailure) {
        try {
          await this._votingService.deleteVoting(votingId);
        } catch {
          // ignore rollback failure: the original upload error is reported
        }
      }
      throw uploadError;
    }
  }

  /**
   * Build the reactive form, prefilling it (and the options/files) when editing.
   */
  private initializeForm(): void {
    this.formVoting = this._formBuilder.group({
      title: [this.voting?.title || "", Validators.required],
      description: [this.voting?.description || ""],
      multipleChoice: [!!this.voting?.multipleChoice],
    });

    // ion-datetime-button renders empty until its datetime has a value, so on
    // create we seed sensible defaults (starts tomorrow 09:00, ends a week
    // later 18:00) — this also keeps a brand-new voting in DRAFT, hence
    // editable. The admin adjusts them before saving.
    if (this.isEditing) {
      this.dateFromValue = this.voting?.dateFrom || "";
      this.dateToValue = this.voting?.dateTo || "";
    } else {
      const start = new Date();
      start.setDate(start.getDate() + 1);
      start.setHours(9, 0, 0, 0);
      const end = new Date();
      end.setDate(end.getDate() + 8);
      end.setHours(18, 0, 0, 0);
      this.dateFromValue = this.buildLocalIso(start);
      this.dateToValue = this.buildLocalIso(end);
    }

    if (this.isEditing) {
      if (this.voting?.options?.length) {
        this.options = this.voting.options.map((option) => ({
          id: option.id,
          text: option.text,
        }));
      }
      this.existingFiles = this.voting?.files ? [...this.voting.files] : [];
    }
  }

  /**
   * Start date must be strictly before the end date.
   */
  /**
   * Build a local ISO 8601 string (no timezone suffix) accepted by ion-datetime,
   * so the value is shown/stored in local time without a UTC shift.
   */
  private buildLocalIso(date: Date): string {
    const pad = (value: number) => `${value}`.padStart(2, "0");
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}:00`
    );
  }

  private areDatesValid(): boolean {
    const from = new Date(this.dateFromValue).getTime();
    const to = new Date(this.dateToValue).getTime();
    return !isNaN(from) && !isNaN(to) && from < to;
  }

  private loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.newVoting",
        "dictionary.editVoting",
        "dictionary.votingTitle",
        "dictionary.votingDescriptionField",
        "dictionary.votingFromDateTime",
        "dictionary.votingToDateTime",
        "dictionary.votingMultipleChoice",
        "dictionary.votingAnswers",
        "dictionary.votingAddAnswer",
        "dictionary.votingFiles",
        "dictionary.newFile",
        "dictionary.save",
        "dictionary.cancel",
        "dictionary.confirm",
        "dictionary.votingValidationError",
      ])
      .subscribe((translation) => {
        this.newTitleLabel = translation["dictionary.newVoting"];
        this.editTitleLabel = translation["dictionary.editVoting"];
        this.titleFieldLabel = translation["dictionary.votingTitle"];
        this.descriptionLabel =
          translation["dictionary.votingDescriptionField"];
        this.dateFromLabel = translation["dictionary.votingFromDateTime"];
        this.dateToLabel = translation["dictionary.votingToDateTime"];
        this.multipleChoiceLabel =
          translation["dictionary.votingMultipleChoice"];
        this.answersLabel = translation["dictionary.votingAnswers"];
        this.addAnswerLabel = translation["dictionary.votingAddAnswer"];
        this.filesLabel = translation["dictionary.votingFiles"];
        this.newFilesLabel = translation["dictionary.newFile"];
        this.saveLabel = translation["dictionary.save"];
        this.cancelLabel = translation["dictionary.cancel"];
        this.doneLabel = translation["dictionary.confirm"];
        this.validationLabel =
          translation["dictionary.votingValidationError"];
      });
  }

  private subscribeTranslationEvent(): void {
    this._translationSubscription =
      this._translateService.onLangChange.subscribe(() => {
        this.loadTranslation();
      });
  }
}
